import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { isUndefined } from 'lodash';
import { TimezoneOption, getDefaultTimezoneOption } from 'domains/timezone';
import { formatInTimeZone } from 'domains/dates/format';
import { format } from 'date-fns';

export interface DisplayTimezoneContextValue {
  displayTimezone?: TimezoneOption;
  setDisplayTimezone: React.Dispatch<React.SetStateAction<TimezoneOption | undefined>>;
  format: typeof format;
}

const DisplayTimezoneContext = createContext<DisplayTimezoneContextValue | undefined>(undefined);

interface DisplayTimezoneProviderProps {
  defaultTimezone?: TimezoneOption;
}

export const DisplayTimezoneProvider: React.FC<DisplayTimezoneProviderProps> = ({ defaultTimezone, children }) => {
  const [displayTimezone, setDisplayTimezone] = useState<TimezoneOption | undefined>(
    getDefaultTimezoneOption(defaultTimezone),
  );

  const formatTime: DisplayTimezoneContextValue['format'] = useCallback(
    (time, template) => {
      if (isUndefined(displayTimezone)) {
        throw new Error('No timezone selected');
      }
      return formatInTimeZone(time, displayTimezone.value, template);
    },
    [displayTimezone],
  );

  const value = useMemo<DisplayTimezoneContextValue>(
    () => ({
      setDisplayTimezone,
      displayTimezone,
      format: formatTime,
    }),
    [displayTimezone, setDisplayTimezone, formatTime],
  );

  return <DisplayTimezoneContext.Provider value={value}>{children}</DisplayTimezoneContext.Provider>;
};

export const useDisplayTimezone = () => {
  const ctx = useContext(DisplayTimezoneContext);

  if (!ctx) {
    throw new Error(`You must wrap your component to a timezone context!`);
  }

  return ctx;
};

type WithDisplayTimezoneType = <P = Record<string, unknown>>(
  Component: React.FC<P>,
  defaultTimezone?: TimezoneOption,
) => React.FC<P>;

export const withDisplayTimezone: WithDisplayTimezoneType = (Component, defaultTimezone) => {
  return props => {
    return (
      <DisplayTimezoneProvider defaultTimezone={defaultTimezone}>
        <Component {...props} />
      </DisplayTimezoneProvider>
    );
  };
};
